// Matt: "Tutorials that would allow anyone who needed it to learn how to
// play the game. They should be interactive, not just rulebook dumps."
//
// TUTORIAL_SEED: a specific, hand-picked seed (not random) so the
// tutorial always deals the exact same starting hand/village layout -
// found by brute-forcing createGameState() across seeds 1-80 (see the
// commit that added this file) for one with a comfortably-affordable
// Level 1 hero in every hero stack (8 starting gold, all 4 hero groups
// buyable) and a mixed hand (weapon, item, hero-in-hand-as-gold). Step
// text below is written in generic terms ("any Level 1 hero you can
// afford") rather than hard-coding exact card names/costs specifically
// so a future source-data change that shifts what this seed produces
// doesn't make the WORDING wrong, even though the mechanics/target
// selectors would still work regardless.
export const TUTORIAL_SEED = 40;

// Each step: { id, title, text, target (a CSS selector to spotlight -
// omit for a plain centered callout), isComplete(state, baseline) -
// omit for a manual "Next" button. `baseline` is a snapshot of
// gameState taken the moment this step became current (see
// TutorialOverlay.vue's own captureBaseline) - steps compare against it
// instead of some absolute constant, since "did something change since
// I was told to do this" is the only thing that's actually true
// regardless of which turn/step this happens to be.
export const THUNDERSTONE_TUTORIAL_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to Thunderstone!',
    text: 'This is a quick, hands-on walkthrough of your very first turn. You\'ll actually play as you go - nothing here is just text to read. Tap "Next" to begin, or "Skip tutorial" any time.',
  },
  {
    id: 'dungeon-hall',
    title: 'The Dungeon Hall',
    text: 'Monsters wait here, in ranks. Fighting one costs Attack (from your heroes\' Strength plus any weapons/spells) that has to meet or beat its Health. Defeated monsters become trophies in your deck.',
    target: '.dungeon-hall',
  },
  {
    id: 'village',
    title: 'The Village',
    text: 'Heroes, weapons, items, spells, and villagers are bought here with Gold. Buying stronger cards for your deck is how you get strong enough to fight deeper into the Dungeon Hall.',
    target: '.village-panel',
  },
  {
    id: 'hand',
    title: 'Your Hand',
    text: 'These are the cards you can actually use this turn. Every card has a Gold Value - spend it in the Village, or bring heroes/weapons into the Dungeon to fight.',
    target: '.player-dock__hand',
  },
  {
    id: 'actions',
    title: 'Your options each turn',
    text: 'Every turn you do exactly one of these: Visit the Village to buy, Enter the Dungeon to fight, Prepare for a fresh hand, or Rest to destroy a weak card. Let\'s try a Village turn.',
    target: '.player-dock__button-grid',
  },
  {
    id: 'visit-village',
    title: 'Give it a try',
    text: 'Tap "Village" now.',
    target: '[data-glossary="action-visit-village"]',
    isComplete: (state) => state.meta.phase === 'village',
  },
  {
    id: 'buy-a-card',
    title: 'Buy something',
    text: 'You have enough Gold to afford a Level 1 hero right now. Tap any card in the Village you can afford - the Guilds and Taverns section (heroes) is a good first buy.',
    target: '.village-panel',
    isComplete: (state, baseline) => state.log.length > baseline.logLength,
  },
  {
    id: 'end-turn',
    title: 'End your turn',
    text: 'Buying discards the rest of your hand and draws a new one when your turn ends. Tap "End village turn" to finish up.',
    target: '.player-dock',
    isComplete: (state, baseline) => state.meta.turnNumber > baseline.turnNumber || state.meta.activePlayerIndex !== baseline.activePlayerIndex,
  },
  {
    id: 'wrap-up',
    title: 'That\'s a full turn!',
    text: 'You\'ll keep drawing stronger cards from the Village, then head into the Dungeon once you can beat its monsters. If you ever forget what something means, tap the ? Glossary button and then tap the thing you\'re unsure about - it works on anything, any time.',
  },
];
